import type { AdminInspectorSettableStatus } from '@carcheck/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { patchInspectorStatus, useAdminInspectorsCache } from '../../../api/admin-inspectors';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the suspend / reinstate orchestration for the inspector
 * detail drawer (AUT-125): fires the status PATCH, a success / error toast,
 * and invalidates the `['admin','inspectors']` cache so the list + drawer
 * refetch. The action is role + permission + audit gated server-side
 * (AUT-138 removed per-action TOTP step-up).
 *
 * Distinct from `useApproveKyc` — the status PATCH response envelope
 * carries an `AdminInspectorListItemDto` (list shape), not the detail, so
 * there is nothing to cross-patch. The refetch is the source of truth.
 */
export interface UseInspectorStatusActionsResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a status change; resolves true on success, false on error. */
  run: (id: string, next: AdminInspectorSettableStatus, reason: string) => Promise<boolean>;
}

export const useInspectorStatusActions = (): UseInspectorStatusActionsResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { invalidate } = useAdminInspectorsCache();

  const run = async (
    id: string,
    next: AdminInspectorSettableStatus,
    reason: string,
  ): Promise<boolean> => {
    setBusy(true);
    const isSuspend = next === 'suspended';
    try {
      await patchInspectorStatus(id, { status: next, reason });
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t(
          isSuspend
            ? 'inspectors.deactivateDialog.successToast'
            : 'inspectors.reactivateDialog.successToast',
        ),
      });
      return true;
    } catch {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t(
          isSuspend
            ? 'inspectors.deactivateDialog.errorToast'
            : 'inspectors.reactivateDialog.errorToast',
        ),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
